import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DesktopSidebarComponent } from "../../../../shared/components/desktop-sidebar/desktop-sidebar.component";
import { DesktopTopbarComponent } from "../../../../shared/components/desktop-topbar/desktop-topbar.component";
import { MobileTopbarComponent } from "../../../../shared/components/mobile-topbar/mobile-topbar.component";
import { FolderButtonComponent } from "../../components/folder-button/folder-button.component";
import { FolderInfo, FolderType } from '../../../../core/interfaces/folder-info';
import { CommonModule } from '@angular/common';
import { CustomDropdownComponent } from "../../../../shared/components/custom-dropdown/custom-dropdown.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ExternalFileStatus, FileResponse, FileStatus, FileUploadEnergyType, FileUploadFileType } from '../../../../core/interfaces/file-upload-interfaces';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { ReplaceFileDialogComponent } from '../../../file-upload/components/dialogs/replace-file-dialog/replace-file-dialog.component';
import { RecentUploadsService } from '../../../../core/services/recent-uploads.service';
import { Contract } from '../../../../core/interfaces/contract-interfaces';
import { ElectricityInvoice, GasInvoice } from '../../../../core/interfaces/invoice-interfaces';
import { HttpErrorResponse } from '@angular/common/http';
import { LocationService } from '../../../../core/api/location.service';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { Location } from "../../../../core/interfaces/location";
import { IsoToFormattedDatePipe } from '../../../../core/pipes/iso-to-formatted-date.pipe';
import { IsoToFormattedPeriodPipe } from '../../../../core/pipes/iso-to-formatted-period.pipe';
import { ArchiveService } from '../../../../core/api/archive.service';
import { combineLatest, lastValueFrom } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { FilesService } from '../../../../core/api/files.service';
import { DeleteFileDialogComponent } from "../../components/dialogs/delete-file-dialog/delete-file-dialog.component";
import { CustomFooterComponent } from "../../../../shared/components/custom-footer/custom-footer.component";
import { ContractStatusComponent } from "../../components/contract-status/contract-status.component";
import { InvoiceStatusComponent } from "../../../invoice/components/invoice-status/invoice-status.component";
import { FileTypeTextComponent } from "../../components/file-type-text/file-type-text.component";
import { EnergyTypeTextComponent } from "../../components/energy-type-text/energy-type-text.component";
import { InvoiceUploadDialogComponent } from "../../../invoice/components/invoice-upload-dialog/invoice-upload-dialog.component";
import { UserDataService } from '../../../../core/services/user-data.service';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { InvoiceHelperService } from '../../../../core/services/invoice-helper.service';

enum SelectedFolderId {
  IN_PROCESSING = 0,
  ELECTRICITY_INVOICE = 1,
  GAS_INVOICE = 2,

  //TODO: EVuk - return after contracts are visible again
  ELECTRICITY_CONTRACT = 91, // 1
  GAS_CONTRACT = 93, // 3
}

@Component({
  selector: 'app-archive-overview-page',
  standalone: true,
  imports: [DesktopSidebarComponent, DesktopTopbarComponent, MobileTopbarComponent, FolderButtonComponent, CommonModule, CustomDropdownComponent, TranslocoPipe, ReplaceFileDialogComponent, ToastComponent, IsoToFormattedDatePipe, IsoToFormattedPeriodPipe, FormsModule, CustomButtonComponent, DeleteFileDialogComponent, CustomFooterComponent, ContractStatusComponent, InvoiceStatusComponent, FileTypeTextComponent, EnergyTypeTextComponent, InvoiceUploadDialogComponent],
  templateUrl: './archive-overview-page.component.html',
  styleUrl: './archive-overview-page.component.css'
})
export class ArchiveOverviewPageComponent implements OnInit, OnDestroy {
  readonly FileStatus = FileStatus;
  selectedSidebarItemId: number = 5;

  // List of folders
  listOfFolders: FolderInfo[] = [];

  numberOfFolders: number = 0;

  selectedFolder: number | undefined = undefined;

  //Files in archive
  listOfContracts: Contract[] = [];
  showContracts: Contract[] = [];

  listOfInvoices: (ElectricityInvoice | GasInvoice)[] = [];
  showInvoices: (ElectricityInvoice | GasInvoice)[] = [];

  listOfFiles: FileResponse[] = [];
  showFiles: FileResponse[] = [];
  // Locations 
  locations: Location[] = [];

  // Filter Dropdown Menu
  filterDropdownOptions: string[] = [];

  filterInvoicesOptions: string[] = [];
  filterContractsOptions: string[] = [];

  selectedFilterDropdown: number | undefined;

  lastLoadedPageNumber: number = 0;
  dataIsLoading: boolean = false;
  loadMoreHidden = false;

  // Search filter
  searchTerm: string = '';

  // Toast
  @ViewChild(ToastComponent) toast!: ToastComponent;

  // Replace File dialog
  @ViewChild(ReplaceFileDialogComponent) replaceFileDialog!: ReplaceFileDialogComponent;

  // Delete file confirmation dialog
  @ViewChild(DeleteFileDialogComponent) deletFileDialog!: DeleteFileDialogComponent;

  get getFolderType(): FolderType {
    if (this.listOfFolders[this.selectedFolder!].fileType! === FileUploadFileType.CONTRACT) {
      return FolderType.CONTRACT;
    }
    else if (this.listOfFolders[this.selectedFolder!].fileType! === FileUploadFileType.INVOICE) {
      return FolderType.INVOICE;
    }
    return FolderType.UNCATEGORIZED;
  }

  get isContract(): boolean {
    return this.getFolderType == FolderType.CONTRACT;
  }

  get isInvoice(): boolean {
    return this.getFolderType == FolderType.INVOICE;
  }

  get isUncategorized(): boolean {
    return this.getFolderType == FolderType.UNCATEGORIZED;
  }

  // Check Admin Status
  get isUserAdmin(): boolean {
    return this.userData.getUser()?.is_superuser ?? false;
  }

  // Update timer
  updateInterval: any;

  // File retry
  sendingRetry: boolean = false;
  sendingReupload: boolean = false;

  // File upload
  //Upload Dialog
  @ViewChild(InvoiceUploadDialogComponent) invoiceUploadDialog!: InvoiceUploadDialogComponent;

  // Replace file extraction data upload
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  selectedFileInputId?: string;
  isUploadingFileReplacement: boolean = false;

  constructor(private translocoService: TranslocoService, private recentUploads: RecentUploadsService, private locationService: LocationService, private apiError: ApiErrorService, private archiveService: ArchiveService, private cd: ChangeDetectorRef, private dateService: DateConversionService, private filesService: FilesService, private userData: UserDataService, private invoiceService: InvoiceService, public invoiceHelper: InvoiceHelperService) { }

  ngOnInit(): void {
    // Load list of folders
    // TODO: EVuk - show contract folders after processing is done
    this.listOfFolders = [
      { name: 'shared.folders.in_processing', numberOfFiles: '...', fileType: undefined, energyType: undefined, folderBackgroundColor: "#BEBDBC", folderColor: "#777271" },
      //{ name: 'shared.folders.electricity_contracts', numberOfFiles: '...', fileType: FileUploadFileType.CONTRACT, energyType: FileUploadEnergyType.ELECTRICITY },
      { name: 'shared.folders.electricity_invoices', numberOfFiles: '...', fileType: FileUploadFileType.INVOICE, energyType: FileUploadEnergyType.ELECTRICITY },
      //{ name: 'shared.folders.gas_contracts', numberOfFiles: '...', folderColor: "#80A7A5", fileType: FileUploadFileType.CONTRACT, energyType: FileUploadEnergyType.GAS },
      { name: 'shared.folders.gas_invoices', numberOfFiles: '...', folderColor: "#80A7A5", fileType: FileUploadFileType.INVOICE, energyType: FileUploadEnergyType.GAS }];

    this.numberOfFolders = this.listOfFolders.length;

    // Set selected folder to All files
    this.selectedFolder = 0;

    // Set Filters
    // Contract filters
    const filterKeys = [
      'archive.filters.default',
      'archive.filters.supplier_asc',
      'archive.filters.supplier_desc',
      'archive.filters.location_asc',
      'archive.filters.location_desc',
      'archive.filters.contract_start_asc',
      'archive.filters.contract_start_desc',
      'archive.filters.contract_end_asc',
      'archive.filters.contract_end_desc',
      'archive.filters.termination_type_asc',
      'archive.filters.termination_type_desc'
    ];

    combineLatest(filterKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterContractsOptions = translations;
      this.updateFilterOptions();
    });

    // Invoice filters
    const filterKeysInv = [
      'invoice.filters.default',
      'archive.filters.file_name_asc',
      'archive.filters.file_name_desc',
      'archive.filters.supplier_asc',
      'archive.filters.supplier_desc',
      'archive.filters.location_asc',
      'archive.filters.location_desc',
      'invoice.filters.date_asc',
      'invoice.filters.date_desc',
      'invoice.filters.invoice_number_asc',
      'invoice.filters.invoice_number_desc',
      'invoice.filters.costs_asc',
      'invoice.filters.costs_desc',
      'invoice.filters.consumption_asc',
      'invoice.filters.consumption_desc'
    ];

    combineLatest(filterKeysInv.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.filterInvoicesOptions = translations;
      this.updateFilterOptions();
    });

    this.selectedFilterDropdown = 0;

    // Load user locations
    this.loadLocations();

    //Load folders
    this.lastLoadedPageNumber = 0;
    this.loadNewData();

    this.cd.detectChanges();

    // Start auto update timer
    this.startUpdateTimer();
  }

  ngOnDestroy(): void {
    // Stop update timer
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  // Triggers when a user clicks on one of the available folders
  selectFolderClick(folderId: number) {
    if (this.dataIsLoading) { return; }
    this.selectedFolder = folderId;

    this.listOfContracts = this.listOfInvoices = this.listOfFiles = [];
    this.lastLoadedPageNumber = 0;
    this.loadMoreHidden = false;
    this.resetFilter(true);
    this.loadNewData();
  }

  // Stop file upload
  stopClicked(id: number) {
    if (!this.recentUploads.stopUpload(id)) {
      return;
    }

    this.toast.showToast(this.translocoService.translate("shared.file_upload.canceled"), this.translocoService.translate("shared.file_upload.stopped"), "warning", 4000, false);

  }

  // Warn user if trying to leave while upload is in progress
  @HostListener('window:beforeunload', ['$event'])
  preventNavigation(event: BeforeUnloadEvent) {
    var isUploading = false;
    for (let recentUpload of this.recentUploads.getUploads()) {
      if (recentUpload.currentUploadProgress != 100) {
        isUploading = true;
        break;
      }
    }

    if (isUploading) {
      event.preventDefault();
    }
  }

  // Get translation for showing commodity type in table
  getCommodity(): string {
    if (this.selectedFolder === undefined) {
      return '';
    }

    if (this.listOfFolders[this.selectedFolder].energyType === FileUploadEnergyType.ELECTRICITY) {
      return 'shared.electricity';
    }
    else if (this.listOfFolders[this.selectedFolder].energyType === FileUploadEnergyType.GAS) {
      return 'shared.gas';
    }

    return '';
  }

  // Load all user locations
  loadLocations() {
    this.locationService.getLocations().subscribe({
      next: (locations) => {
        if (locations) {
          this.locations = locations;
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => { }
    });
  }

  // Get location by id from loaded locations list
  getLocationById(id: string): Location | undefined {
    return this.locations.find(location => location.id === id);
  }

  // Retruns fromated location text for table
  getLocationText(id: string): string {
    var location = this.getLocationById(id);
    if (!location) {
      return '';
    }

    return `${location.street ?? ''} ${location.number ?? ''}, ${location.postal_code ?? ''} ${location.city ?? ''}`;
  }

  getCommodityUnit(): string {
    if (this.selectedFolder === undefined) {
      return '';
    }

    if (this.listOfFolders[this.selectedFolder].energyType === FileUploadEnergyType.ELECTRICITY) {
      return 'kWh';
    }

    if (this.listOfFolders[this.selectedFolder].energyType === FileUploadEnergyType.GAS) {
      return 'smc';
    }
    return '';
  }

  // Loading data
  loadNewData() {
    this.dataIsLoading = true;

    // Update file count numbers
    this.loadFileCounts();

    // Uncategorized files
    if (this.selectedFolder === SelectedFolderId.IN_PROCESSING) {
      this.archiveService.getUncategorizedFiles((this.lastLoadedPageNumber + 1)).subscribe({
        next: (response) => {
          if (response) {
            if (response.data.length === 0 || this.listOfFiles.length + response.data.length >= response.totalCount) {
              this.loadMoreHidden = true;
            }

            this.listOfFiles = [...this.listOfFiles, ...response.data];

            // Applay filter
            this.resetFilter();
            this.applySelectedFilter();
            this.applySearchFilter();
            this.updateFilterOptions();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.dataIsLoading = false;
        },
        complete: () => {
          this.lastLoadedPageNumber++;
          this.dataIsLoading = false;
        }
      });
    }

    // Electricity contracts
    else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_CONTRACT) {
      this.archiveService.getElectricityContracts((this.lastLoadedPageNumber + 1)).subscribe({
        next: (response) => {
          if (response) {
            if (response.data.length === 0 || this.listOfContracts.length + response.data.length >= response.totalCount) {
              this.loadMoreHidden = true;
            }

            this.listOfContracts = [...this.listOfContracts, ...response.data];

            // Applay filter
            this.resetFilter();
            this.applySelectedFilter();
            this.applySearchFilter();
            this.updateFilterOptions();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.dataIsLoading = false;
        },
        complete: () => {
          this.lastLoadedPageNumber++;
          this.dataIsLoading = false;
        }
      });
    }

    // Electricity invoices
    else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_INVOICE) {
      this.archiveService.getElectricityInvoices((this.lastLoadedPageNumber + 1)).subscribe({
        next: (response) => {
          if (response) {
            if (response.data.length === 0 || this.listOfInvoices.length + response.data.length >= response.totalCount) {
              this.loadMoreHidden = true;
            }

            this.listOfInvoices = [...this.listOfInvoices, ...response.data];

            // Applay filter
            this.resetFilter();
            this.applySelectedFilter();
            this.applySearchFilter();
            this.updateFilterOptions();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.dataIsLoading = false;
        },
        complete: () => {
          this.lastLoadedPageNumber++;
          this.dataIsLoading = false;
        }
      });
    }

    // Gas contracts
    else if (this.selectedFolder === SelectedFolderId.GAS_CONTRACT) {
      this.archiveService.getGasContracts((this.lastLoadedPageNumber + 1)).subscribe({
        next: (response) => {
          if (response) {
            if (response.data.length === 0 || this.listOfContracts.length + response.data.length >= response.totalCount) {
              this.loadMoreHidden = true;
            }

            this.listOfContracts = [...this.listOfContracts, ...response.data];

            // Applay filter
            this.resetFilter();
            this.applySelectedFilter();
            this.applySearchFilter();
            this.updateFilterOptions();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.dataIsLoading = false;
        },
        complete: () => {
          this.lastLoadedPageNumber++;
          this.dataIsLoading = false;
        }
      });
    }

    // Gas invoices
    else if (this.selectedFolder === SelectedFolderId.GAS_INVOICE) {
      this.archiveService.getGasInvoices((this.lastLoadedPageNumber + 1)).subscribe({
        next: (response) => {
          if (response) {
            if (response.data.length === 0 || this.listOfInvoices.length + response.data.length >= response.totalCount) {
              this.loadMoreHidden = true;
            }

            this.listOfInvoices = [...this.listOfInvoices, ...response.data];

            // Applay filter
            this.resetFilter();
            this.applySelectedFilter();
            this.applySearchFilter();
            this.updateFilterOptions();
          }
        },
        error: async (error) => {
          var crid = 'UNKNOWN-CRID';

          if (error instanceof HttpErrorResponse) {
            crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
          }
          this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
          this.dataIsLoading = false;
        },
        complete: () => {
          this.lastLoadedPageNumber++;
          this.dataIsLoading = false;
        }
      });
    }
  }

  resetFilter(resetFilter: boolean = false) {
    this.showContracts = [];
    this.showContracts = this.listOfContracts;

    this.showInvoices = [];
    this.showInvoices = this.listOfInvoices;

    this.showFiles = [];
    this.showFiles = this.listOfFiles;
    if (resetFilter) {
      this.selectedFilterDropdown = 0;
    }
  }

  applySelectedFilter() {
    if (this.selectedFilterDropdown === undefined || this.selectedFolder === undefined) {
      return;
    }

    const optionIndex = this.selectedFilterDropdown;

    // Contracts
    if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.CONTRACT) {
      if (optionIndex === 0) {
        this.resetFilter();
      } else if (optionIndex === 1) {
        this.showContracts.sort((a, b) => (a.supplier ?? '').localeCompare(b.supplier ?? ''));
      } else if (optionIndex === 2) {
        this.showContracts.sort((a, b) => (b.supplier ?? '').localeCompare(a.supplier ?? ''));
      } else if (optionIndex === 3) {
        this.showContracts.sort((a, b) => (this.getLocationText(a.location_id ?? '')).localeCompare(this.getLocationText(b.location_id ?? '')));
      } else if (optionIndex === 4) {
        this.showContracts.sort((a, b) => (this.getLocationText(b.location_id ?? '')).localeCompare(this.getLocationText(a.location_id ?? '')));
      } else if (optionIndex === 5) {
        this.showContracts.sort((a, b) => new Date(a.contract_start ?? '').getTime() - new Date(b.contract_start ?? '').getTime());
      } else if (optionIndex === 6) {
        this.showContracts.sort((a, b) => new Date(b.contract_start ?? '').getTime() - new Date(a.contract_start ?? '').getTime());
      } else if (optionIndex === 7) {
        this.showContracts.sort((a, b) => new Date(a.contract_end ?? '').getTime() - new Date(b.contract_end ?? '').getTime());
      } else if (optionIndex === 8) {
        this.showContracts.sort((a, b) => new Date(b.contract_end ?? '').getTime() - new Date(a.contract_end ?? '').getTime());
      } else if (optionIndex === 9) {
        this.showContracts.sort((a, b) => (a.termination_type ?? '').localeCompare(b.termination_type ?? ''));
      } else if (optionIndex === 10) {
        this.showContracts.sort((a, b) => (b.termination_type ?? '').localeCompare(a.termination_type ?? ''));
      }
    }

    // Invoices
    else if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.INVOICE) {
      if (optionIndex === 0) {
        this.resetFilter();
      }
      else if (optionIndex === 1) {
        this.showInvoices.sort((a, b) => (a.file.filename ?? '').localeCompare((b.file.filename ?? '')));
      } else if (optionIndex === 2) {
        this.showInvoices.sort((a, b) => (b.file.filename ?? '').localeCompare((a.file.filename ?? '')));
      } else if (optionIndex === 3) {
        this.showInvoices.sort((a, b) => this.invoiceHelper.getInvoiceSupplierName(a).localeCompare(this.invoiceHelper.getInvoiceSupplierName(b)));
      } else if (optionIndex === 4) {
        this.showInvoices.sort((a, b) => this.invoiceHelper.getInvoiceSupplierName(b).localeCompare(this.invoiceHelper.getInvoiceSupplierName(a)));
      } else if (optionIndex === 5) {
        this.showInvoices.sort((a, b) => this.getLocationText(a.location_id ?? '').localeCompare(this.getLocationText(b.location_id ?? '')));
      } else if (optionIndex === 6) {
        this.showInvoices.sort((a, b) => this.getLocationText(b.location_id ?? '').localeCompare(this.getLocationText(a.location_id ?? '')));
      } else if (optionIndex === 7) {
        this.showInvoices.sort((a, b) => new Date(a.for_period ?? '').getTime() - new Date(b.for_period ?? '').getTime());
      } else if (optionIndex === 8) {
        this.showInvoices.sort((a, b) => new Date(b.for_period ?? '').getTime() - new Date(a.for_period ?? '').getTime());
      } else if (optionIndex === 9) {
        this.showInvoices.sort((a, b) => (a.invoice_number ?? '').localeCompare(b.invoice_number ?? ''));
      } else if (optionIndex === 10) {
        this.showInvoices.sort((a, b) => (b.invoice_number ?? '').localeCompare(a.invoice_number ?? ''));
      } else if (optionIndex === 11) {
        this.showInvoices.sort((a, b) => (a.invoice_amount_net_actual ?? 0) - (b.invoice_amount_net_actual ?? 0));
      } else if (optionIndex === 12) {
        this.showInvoices.sort((a, b) => (b.invoice_amount_net_actual ?? 0) - (a.invoice_amount_net_actual ?? 0));
      } else if (optionIndex === 13) {
        this.showInvoices.sort((a, b) => (a.total_consumption ?? 0) - (b.total_consumption ?? 0));
      } else if (optionIndex === 14) {
        this.showInvoices.sort((a, b) => (b.total_consumption ?? 0) - (a.total_consumption ?? 0));
      }
    }
  }

  applySearchFilter() {
    if (this.selectedFolder === undefined) { return; }

    this.resetFilter();
    const term = this.searchTerm.trim().toLowerCase();

    // Contracts
    if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.CONTRACT) {
      this.showContracts = this.showContracts.filter(contract => {
        return (
          contract.supplier!.toLowerCase().includes(term) ||
          this.getLocationText(contract.location_id!).toLowerCase().includes(term) ||
          this.dateService.isoToFormattedDate(contract.contract_start ?? '').includes(term) ||
          this.dateService.isoToFormattedDate(contract.contract_end ?? '').includes(term) ||
          contract.termination_type!.toLowerCase().includes(term)
        );
      });
    }
    // Invoices
    else if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.INVOICE) {
      this.showInvoices = this.showInvoices.filter(invoice => {
        return (
          this.invoiceHelper.getInvoiceSupplierName(invoice).toLowerCase().includes(term) ||
          invoice.invoice_number!.toLowerCase().includes(term) ||
          this.getLocationText(invoice.location_id!).toLowerCase().includes(term) ||
          this.dateService.isoToFormattedPeriod(invoice.for_period ?? '').toLowerCase().includes(term) ||
          invoice.invoice_amount_net_actual!.toString().includes(term) ||
          invoice.total_consumption!.toString().includes(term)
        );
      });
    }
    else if (this.getFolderType == FolderType.UNCATEGORIZED) {
      this.showFiles = this.showFiles.filter(file => {
        return (
          (file.filename ?? '').toLowerCase().includes(term)
        );
      });
    }
  }

  updateFilterOptions() {
    if (this.selectedFolder === undefined) {
      this.filterDropdownOptions = [];
      return;
    }

    if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.CONTRACT) {
      this.filterDropdownOptions = this.filterContractsOptions;
    }
    else if (this.listOfFolders[this.selectedFolder].fileType === FileUploadFileType.INVOICE) {
      this.filterDropdownOptions = this.filterInvoicesOptions;
    }
    else if (this.getFolderType == FolderType.UNCATEGORIZED) {
      this.filterDropdownOptions = [];
    }
  }

  loadFileCounts() {
    this.archiveService.getFileCounts().subscribe({
      next: (response) => {
        if (response) {
          // TODO: EVuk - return after contracts
          this.listOfFolders[SelectedFolderId.IN_PROCESSING].numberOfFiles = response.uncategorized_count ?? '?';
          //this.listOfFolders[SelectedFolderId.ELECTRICITY_CONTRACT].numberOfFiles = response.electricity_contract_count ?? '?';
          this.listOfFolders[SelectedFolderId.ELECTRICITY_INVOICE].numberOfFiles = response.electricity_invoice_count ?? '?';
          //this.listOfFolders[SelectedFolderId.GAS_CONTRACT].numberOfFiles = response.gas_contract_count ?? '?';
          this.listOfFolders[SelectedFolderId.GAS_INVOICE].numberOfFiles = response.gas_invoice_count ?? '?';
        }
      },
      error: async (error) => {
        var crid = 'UNKNOWN-CRID';

        if (error instanceof HttpErrorResponse) {
          crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        }
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      },
      complete: () => {

      }
    });
  }

  async downloadFile(fileId: string) {
    try {
      var response = await lastValueFrom(this.filesService.downloadFile(fileId));
      window.open(response.download_url, '_blank');

    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.dataIsLoading = false;
    }
  }

  async deleteFile(fileId: string) {
    var confirmation = await this.deletFileDialog.openModal();

    if (!confirmation) {
      return;
    }

    try {
      await lastValueFrom(this.filesService.deleteFile(fileId));
      this.toast.showToast('shared.success', 'archive.file_success_delete', 'success', 4000, true);

      this.listOfContracts = this.listOfInvoices = this.listOfFiles = [];
      this.lastLoadedPageNumber = 0;
      this.loadMoreHidden = false;
      this.loadNewData();

      // Reload user data
      await this.userData.loadUserData(false);

    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      this.dataIsLoading = false;
    }
  }

  getStatusTooltip(fileStatus: string | null): string {
    if (fileStatus === FileStatus.DUPLICATE_PERIOD) {
      return 'archive.external_file_status.DUPLICATE_PERIOD_TOOLTIP';
    }
    return '';
  }

  getExternalStatusTranslocoKey(externalStatus: string | null, fileStatus: string | null): string {
    if (fileStatus === FileStatus.ERROR) {
      return `archive.external_file_status.ERROR`;
    }
    if (fileStatus === FileStatus.DUPLICATE_PERIOD) {
      return `archive.external_file_status.DUPLICATE_PERIOD`;
    }
    const validStatuses = Object.values(ExternalFileStatus);
    const safeStatus = validStatuses.includes(externalStatus as ExternalFileStatus) ? externalStatus : 'PENDING';
    return `archive.external_file_status.${safeStatus}`;
  }

  onUploadCompleted(event: any) {
    if (!event.success) {
      return;
    }

    // Refresh page data
    this.selectFolderClick(this.selectedFolder || 0);
  }

  startUpdateTimer() {
    // Update folder data every 5 seconds
    this.updateInterval = setInterval(async () => {
      try {
        await this.updateData();
      } catch (err) {
        console.error('Archive update failed:', err);
      }
    }, 5000);
  }

  private async updateData(): Promise<void> {
    if (this.dataIsLoading || this.selectedFolder == undefined) { return; }
    // Load and update the text for file counts
    this.loadFileCounts();

    // Refresh locations so newly extracted invoices resolve their location text
    this.loadLocations();

    const tempCurrentPage = this.lastLoadedPageNumber;

    // Load all data for the selected folder, then temporarily save it
    var templistOfFiles: any = [];
    var totalCount = 0;
    for (var i = 1; i <= tempCurrentPage; i++) {
      const response = await this.loadNewDataWithouRefresh(i)
      totalCount = response.totalCount;
      templistOfFiles = [...templistOfFiles, ...response.data];
    }

    // After all data is loaded, update it at once to avoid screen flickering
    this.listOfContracts = this.listOfInvoices = this.listOfFiles = [];

    if (this.selectedFolder === SelectedFolderId.IN_PROCESSING) {
      this.listOfFiles = templistOfFiles;
    }
    else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_INVOICE || this.selectedFolder === SelectedFolderId.GAS_INVOICE) {
      this.listOfInvoices = templistOfFiles;
    }
    else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_CONTRACT || this.selectedFolder === SelectedFolderId.GAS_CONTRACT) {
      this.listOfContracts = templistOfFiles;
    }

    // Check if there are more items
    if (templistOfFiles.length === 0 || templistOfFiles.length >= totalCount) {
      this.loadMoreHidden = true;
    }

    // Reset state of filters and search
    this.resetFilter();
    this.applySelectedFilter();
    this.applySearchFilter();
  }

  async loadNewDataWithouRefresh(pageId: number) {
    try {
      var response;
      // Uncategorized files
      if (this.selectedFolder === SelectedFolderId.IN_PROCESSING) {
        response = await lastValueFrom(this.archiveService.getUncategorizedFiles((pageId)))
      }
      // Electricity contracts
      else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_CONTRACT) {
        response = await lastValueFrom(this.archiveService.getElectricityContracts((pageId)))
      }
      // Electricity invoices
      else if (this.selectedFolder === SelectedFolderId.ELECTRICITY_INVOICE) {
        response = await lastValueFrom(this.archiveService.getElectricityInvoices((pageId)))
      }
      // Gas contracts
      else if (this.selectedFolder === SelectedFolderId.GAS_CONTRACT) {
        response = await lastValueFrom(this.archiveService.getGasContracts((pageId)))
      }
      // Gas invoices
      else if (this.selectedFolder === SelectedFolderId.GAS_INVOICE) {
        response = await lastValueFrom(this.archiveService.getGasInvoices((pageId)))
      }
      return { data: response.data, totalCount: response.totalCount };
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }

    return { data: [], totalCount: 0 };
  }

  canUserTriggerRetry(file: FileResponse): boolean {
    var isOldEnoughtForRetry = true;
    if (file.external_status_updated_at) {
      const updatedAt = new Date(file.external_status_updated_at);
      var numberOfMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

      // Allow retry for invoices that stay uploaded only after some time
      if (file.external_status == ExternalFileStatus.UPLOADED) {
        numberOfMinutesAgo = new Date(Date.now() - 20 * 60 * 1000);
      }

      isOldEnoughtForRetry = updatedAt < numberOfMinutesAgo;
    }

    if (file.external_status == null) { return false; }

    const isStatusAllowedToRetry = [
      ExternalFileStatus.RETRYING,
      ExternalFileStatus.EXTRACTION_FAILED,
      ExternalFileStatus.UPLOADED
    ].includes(file.external_status as ExternalFileStatus);

    if (isStatusAllowedToRetry && isOldEnoughtForRetry)
      return true;

    return false;
  }

  canUserTriggerReupload(file: FileResponse): boolean {
    var isOldEnoughtForReupload = true;
    if (file.external_status_updated_at) {
      const updatedAt = new Date(file.external_status_updated_at);
      // 24 hours
      var numberOfHoursAgo = new Date(Date.now() - (24 * 60 * 60 * 1000));

      isOldEnoughtForReupload = updatedAt < numberOfHoursAgo;
    }

    if (file.external_status == null && isOldEnoughtForReupload) { return true; }
    else if (file.external_status == null) { return false; }

    const isStatusAllowedToReupload = [
      ExternalFileStatus.EXTRACTION_FAILED,
      ExternalFileStatus.EXTRACTED
    ].includes(file.external_status as ExternalFileStatus);

    if (isStatusAllowedToReupload && isOldEnoughtForReupload) {
      return true;
    }

    return false;
  }

  async triggerRetry(file: FileResponse) {
    if (!this.canUserTriggerRetry(file) || this.sendingRetry) {
      return;
    }

    this.sendingRetry = true;

    try {
      await lastValueFrom(this.archiveService.retryFileProcessing(file.id));
      await this.updateData()

      this.toast.showToast('shared.success', 'shared.toast.file_retry_sent', 'success', 8000, true);
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
        if (error.error.code == 'SVC-4006') {
          showError = false;
          this.toast.showToast('shared.error', 'shared.toast.file_retry', 'error', 5000, true);
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
    }
    this.sendingRetry = false;
  }

  async triggerReupload(file: FileResponse) {
    if (!this.canUserTriggerReupload(file) || this.sendingReupload) {
      return;
    }

    this.sendingReupload = true;

    // Notify admins that reupload happened
    try {
      await lastValueFrom(this.filesService.sendReuploadEmail(file.id));
    } catch (error) {
    }

    // Delete old file
    try {
      await lastValueFrom(this.filesService.deleteFile(file.id));
    } catch (error) {
      var crid = 'UNKNOWN-CRID';

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
    }

    // Trigger new upload
    this.invoiceUploadDialog.openModal(true);

    this.toast.showToast('shared.success', 'shared.toast.file_retry_sent', 'success', 8000, true);
    this.sendingReupload = false;
  }

  openInvoiceUploadDialog() {
    this.invoiceUploadDialog.openModal();
  }

  triggerFileInput(fileId: string) {
    if (!this.isUserAdmin) { return; }
    this.fileInput.nativeElement.click();
    this.selectedFileInputId = fileId;
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.toast.showToast('shared.error', 'shared.toast.invalid_json', 'error', 5000, true);
      return;
    }

    // Check file type
    const allowedTypes = ['application/json', '.json'];

    if (!allowedTypes.includes(file.type)) {
      this.toast.showToast('shared.error', 'shared.toast.invalid_json', 'error', 5000, true);
      return;
    }

    await this.uploadFileReplace(file);
    input.value = '';
  }

  async uploadFileReplace(file: File) {
    if (!this.isInvoice) {
      return;
    }

    this.isUploadingFileReplacement = true;

    try {
      if (this.selectedFolder == 2) {
        await lastValueFrom(this.invoiceService.uploadElectricityJsonUpdate(file, this.selectedFileInputId ?? ''));
      }
      else {
        await lastValueFrom(this.invoiceService.uploadGasJsonUpdate(file, this.selectedFileInputId ?? ''));
      }

      this.toast.showToast('shared.success', 'shared.toast.file_extraction_result_replaced', 'success', 8000, true);
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      var showError = true;

      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';

        let errorBody: any;
        try {
          errorBody = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
        } catch {
          errorBody = { message: error.error };
        }

        if (errorBody.code == 'SVC-4001') {
          showError = false;
          this.toast.showToast('shared.error', 'shared.toast.invalid_json', 'error', 5000, true);
        }
        else if (errorBody.code == 'SVC-4002') {
          showError = false;
          this.toast.showToast('shared.error', 'shared.toast.invalid_json_structure', 'error', 5000, true);
        }
        else if (errorBody.code == 'SVC-4000') {
          showError = false;
          this.toast.showToast('shared.error', 'shared.toast.file_does_not_exist', 'error', 5000, true);
        }
      }

      if (showError) {
        this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      }
    }
    this.isUploadingFileReplacement = false;
  }
}
